/**
 * CR114: Asset Editor Dialog
 * CR125: Added "Mark as Logo" quick action and tag normalization
 * CR314: Added file upload tab alongside SVG input
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Eye, X, Tag, Image, Upload, FileCode } from "lucide-react";
import type { AppAsset, AssetType, TintMode } from "@/lib/asset-studio/types";
import { 
  ASSET_TYPE_LABELS, 
  TINT_MODE_LABELS, 
  validateSvg, 
  sanitizeSvg,
} from "@/lib/asset-studio/types";
import { normalizeTag, normalizeTags } from "@/lib/asset-studio/logoAssetUtils";
import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_TYPES_LABEL,
  validateUploadFile,
} from "@/hooks/useAssetUpload";
import { MAX_UPLOAD_SIZE_MB } from "@/lib/asset-studio/assetStorage";

interface AssetEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: AppAsset | null;
  onSave: (data: {
    name: string;
    type: AssetType;
    svg: string;
    is_tileable: boolean;
    default_tint: string | null;
    tint_mode: TintMode;
    tags: string[];
    /** CR314: Optional uploaded file (bypasses SVG-only flow) */
    uploadedFile?: File;
  }) => Promise<void>;
  saving: boolean;
  /** CR125: Pre-fill defaults for create mode (e.g., from Layout Studio quick create) */
  initialDefaults?: {
    name?: string;
    type?: AssetType;
    tags?: string[];
    tint_mode?: TintMode;
    svg?: string;
  };
}

export function AssetEditorDialog({
  open,
  onOpenChange,
  asset,
  onSave,
  saving,
  initialDefaults,
}: AssetEditorDialogProps) {
  // Form state
  const [name, setName] = useState("");
  const [type, setType] = useState<AssetType>("starburst");
  const [svg, setSvg] = useState("");
  const [isTileable, setIsTileable] = useState(false);
  const [defaultTint, setDefaultTint] = useState("");
  const [tintMode, setTintMode] = useState<TintMode>("none");
  const [tagsInput, setTagsInput] = useState("");
  const [tagsList, setTagsList] = useState<string[]>([]);
  const [inputMode, setInputMode] = useState<"svg" | "upload">("svg");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog opens/closes or asset changes
  useEffect(() => {
    if (open && asset) {
      // Edit mode: load from existing asset
      setName(asset.name);
      setType(asset.type);
      setSvg(asset.svg);
      setIsTileable(asset.is_tileable);
      setDefaultTint(asset.default_tint || "");
      setTintMode(asset.tint_mode);
      setTagsList(normalizeTags(asset.tags));
      setInputMode("svg");
      setUploadedFile(null);
      setUploadPreview(null);
    } else if (open) {
      // Create mode: use initialDefaults or blank
      setName(initialDefaults?.name || "");
      setType(initialDefaults?.type || "starburst");
      setSvg(initialDefaults?.svg || "");
      setIsTileable(false);
      setDefaultTint("");
      setTintMode(initialDefaults?.tint_mode || "none");
      setTagsList(normalizeTags(initialDefaults?.tags || []));
      setInputMode("svg");
      setUploadedFile(null);
      setUploadPreview(null);
    }
    setTagsInput("");
  }, [open, asset, initialDefaults]);

  // CR125: Check if "logo" tag is present
  const hasLogoTag = useMemo(() => {
    return tagsList.some((t) => normalizeTag(t) === "logo");
  }, [tagsList]);

  // CR125: Handler to add "logo" tag with one click
  const handleMarkAsLogo = () => {
    if (!hasLogoTag) {
      setTagsList([...tagsList, "logo"]);
    }
    // Suggest changing type to icon if not already
    if (type !== "icon") {
      setType("icon");
    }
  };

  // SVG validation (only matters in SVG mode)
  const validationError = useMemo(() => {
    if (inputMode === "upload") return null;
    return validateSvg(svg);
  }, [svg, inputMode]);
  const isValid = inputMode === "upload"
    ? uploadedFile !== null && name.trim().length > 0
    : !validationError && name.trim().length > 0;

  // CR314: File upload handlers
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateUploadFile(file);
    if (!validation.valid) {
      return;
    }
    setUploadedFile(file);
    setUploadPreview(URL.createObjectURL(file));
    // Auto-fill name from filename if empty
    if (!name.trim()) {
      setName(file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " "));
    }
    // If SVG, also read content
    if (file.type === "image/svg+xml") {
      file.text().then((text) => setSvg(text));
    }
  }, [name]);

  const handleClearUpload = useCallback(() => {
    setUploadedFile(null);
    if (uploadPreview) URL.revokeObjectURL(uploadPreview);
    setUploadPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [uploadPreview]);

  // Safe preview (sanitized)
  const safePreviewSvg = useMemo(() => {
    if (validationError) return null;
    return sanitizeSvg(svg);
  }, [svg, validationError]);

  // Tag handlers
  const handleAddTag = () => {
    const tag = tagsInput.trim().toLowerCase();
    if (tag && !tagsList.includes(tag)) {
      setTagsList([...tagsList, tag]);
    }
    setTagsInput("");
  };

  const handleRemoveTag = (tag: string) => {
    setTagsList(tagsList.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Save handler - normalize tags on save
  const handleSave = async () => {
    if (!isValid) return;
    const placeholder = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="transparent"/></svg>`;
    await onSave({
      name: name.trim(),
      type,
      svg: inputMode === "upload" && uploadedFile && uploadedFile.type !== "image/svg+xml"
        ? placeholder
        : sanitizeSvg(svg),
      is_tileable: isTileable,
      default_tint: defaultTint.trim() || null,
      tint_mode: tintMode,
      tags: normalizeTags(tagsList),
      uploadedFile: inputMode === "upload" ? uploadedFile ?? undefined : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{asset ? "Edit Asset" : "Create New Asset"}</DialogTitle>
          <DialogDescription>
            {asset 
              ? "Update asset properties and SVG content" 
              : "Add a new visual asset for Theme Labs skins"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 py-4">
          {/* Left Column: Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Spring Leaf Corner"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select value={type} onValueChange={(v) => setType(v as AssetType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* CR314: Tabbed input — SVG Code or Upload Image */}
            {!asset && (
              <Tabs value={inputMode} onValueChange={(v) => setInputMode(v as "svg" | "upload")}>
                <TabsList className="grid w-full grid-cols-2 h-8">
                  <TabsTrigger value="svg" className="text-xs gap-1">
                    <FileCode className="h-3 w-3" />
                    SVG Code
                  </TabsTrigger>
                  <TabsTrigger value="upload" className="text-xs gap-1">
                    <Upload className="h-3 w-3" />
                    Upload Image
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="svg" className="mt-2 space-y-2">
                  <Label htmlFor="svg">SVG Content *</Label>
                  <Textarea
                    id="svg"
                    value={svg}
                    onChange={(e) => setSvg(e.target.value)}
                    placeholder="<svg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'>...</svg>"
                    className="font-mono text-xs h-32"
                  />
                  {validationError && (
                    <Alert variant="destructive" className="py-2">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        {validationError}
                      </AlertDescription>
                    </Alert>
                  )}
                </TabsContent>

                <TabsContent value="upload" className="mt-2 space-y-2">
                  <Label>Image File *</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {uploadedFile ? (
                    <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/30">
                      <img
                        src={uploadPreview || ""}
                        alt="Upload preview"
                        className="w-12 h-12 rounded object-cover bg-muted"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(uploadedFile.size / 1024).toFixed(1)}KB · {uploadedFile.type}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClearUpload}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full h-20 border-dashed"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Upload className="h-5 w-5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {ACCEPTED_TYPES_LABEL} · Max {MAX_UPLOAD_SIZE_MB}MB
                        </span>
                      </div>
                    </Button>
                  )}
                </TabsContent>
              </Tabs>
            )}

            {/* Edit mode: show SVG textarea only */}
            {asset && (
              <div className="space-y-2">
                <Label htmlFor="svg">SVG Content *</Label>
                <Textarea
                  id="svg"
                  value={svg}
                  onChange={(e) => setSvg(e.target.value)}
                  placeholder="<svg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'>...</svg>"
                  className="font-mono text-xs h-32"
                />
                {validationError && (
                  <Alert variant="destructive" className="py-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      {validationError}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="tileable">Tileable Pattern</Label>
              <Switch
                id="tileable"
                checked={isTileable}
                onCheckedChange={setIsTileable}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tint">Default Tint</Label>
                <Input
                  id="tint"
                  value={defaultTint}
                  onChange={(e) => setDefaultTint(e.target.value)}
                  placeholder="#7BD8B0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tintMode">Tint Mode</Label>
                <Select value={tintMode} onValueChange={(v) => setTintMode(v as TintMode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TINT_MODE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Tags</Label>
                {/* CR125: Quick "Mark as Logo" button */}
                <Button
                  type="button"
                  variant={hasLogoTag ? "secondary" : "outline"}
                  size="sm"
                  onClick={handleMarkAsLogo}
                  disabled={hasLogoTag}
                  className="h-6 text-xs gap-1"
                >
                  <Image className="h-3 w-3" />
                  {hasLogoTag ? "Logo ✓" : "Mark as Logo"}
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add tag..."
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={handleAddTag}
                  disabled={!tagsInput.trim()}
                >
                  Add
                </Button>
              </div>
              {tagsList.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-2">
                  {tagsList.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Preview */}
          <div className="space-y-4">
            <Label className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Live Preview
            </Label>
            <div className="border rounded-lg bg-muted/30 aspect-square flex items-center justify-center overflow-hidden">
              {/* CR314: Show uploaded image preview or SVG preview */}
              {inputMode === "upload" && uploadPreview ? (
                <img 
                  src={uploadPreview}
                  alt="Upload preview"
                  className="w-full h-full p-4 object-contain"
                />
              ) : safePreviewSvg ? (
                <img 
                  src={`data:image/svg+xml,${encodeURIComponent(safePreviewSvg)}`}
                  alt="Asset preview"
                  className="w-full h-full p-4 object-contain"
                  style={{ color: defaultTint || "currentColor" }}
                />
              ) : (
                <div className="text-muted-foreground text-sm text-center p-4">
                  {inputMode === "upload"
                    ? "Select an image to preview"
                    : svg.trim() ? "Invalid SVG" : "Enter SVG to preview"}
                </div>
              )}
            </div>

            {/* Preview at different sizes */}
            {safePreviewSvg && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Size variants</Label>
                <div className="flex gap-2 items-end">
                  {[24, 32, 48, 64].map((size) => (
                    <img 
                      key={size}
                      src={`data:image/svg+xml,${encodeURIComponent(safePreviewSvg)}`}
                      alt={`Preview at ${size}px`}
                      className="border rounded bg-background"
                      style={{ 
                        width: size, 
                        height: size,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Tile preview for tileable assets */}
            {isTileable && safePreviewSvg && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Tile preview</Label>
                <div 
                  className="border rounded h-24 overflow-hidden"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(safePreviewSvg)}")`,
                    backgroundSize: "32px 32px",
                    backgroundRepeat: "repeat",
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || saving}>
            {saving ? "Saving..." : asset ? "Update Asset" : "Create Asset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
